import React from 'react';
import { Chip, ChipProps, useTheme } from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { getSeverityColorFromTheme, type Severity } from './utils/colorUtils';

export interface SeverityChipProps extends Omit<ChipProps, 'color'> {
  /**
   * Severity level to display
   */
  severity: Severity | string;
  /**
   * Whether to show only the first letter (useful for mobile/compact views)
   */
  compact?: boolean;
  /**
   * Whether to show an icon alongside the label
   */
  showIcon?: boolean;
}

/**
 * Get icon for severity level
 */
function getSeverityIcon(severity: string): React.ReactElement | undefined {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'error':
      return <ErrorIcon />;
    case 'high':
    case 'warning':
      return <WarningIcon />;
    case 'medium':
      return <WarningIcon />;
    case 'low':
    case 'info':
      return <InfoIcon />;
    default:
      return <HelpIcon />;
  }
}

/**
 * Reusable chip component for displaying severity levels.
 * Provides consistent styling across alert and monitoring UIs.
 *
 * @example
 * // Standard usage
 * <SeverityChip severity="critical" />
 *
 * @example
 * // Compact mode for mobile
 * <SeverityChip severity="warning" compact />
 *
 * @example
 * // With icon
 * <SeverityChip severity="error" showIcon />
 */
export function SeverityChip({
  severity,
  compact = false,
  showIcon = false,
  size = 'small',
  sx,
  ...props
}: SeverityChipProps) {
  const theme = useTheme();
  const bgColor = getSeverityColorFromTheme(severity, theme);
  const textColor = theme.palette.getContrastText(bgColor);

  const label = compact
    ? severity.charAt(0).toUpperCase()
    : severity.toUpperCase();

  return (
    <Chip
      label={label}
      size={size}
      icon={showIcon ? getSeverityIcon(severity) : undefined}
      sx={{
        minWidth: compact ? 28 : 65,
        backgroundColor: bgColor,
        color: textColor,
        fontWeight: 'bold',
        fontSize: compact ? '0.7rem' : '0.65rem',
        textTransform: 'uppercase',
        '& .MuiChip-label': {
          px: compact ? 0.5 : 1,
        },
        '& .MuiChip-icon': {
          color: textColor,
        },
        ...sx,
      }}
      {...props}
    />
  );
}

export default SeverityChip;
